import { useState } from 'react'
import { Platform, Pressable, Share, Text, View } from 'react-native'
import cn from '@ui/utils/cn'

type JsonValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | JsonValue[]
  | { [key: string]: JsonValue }

const mono = {
  fontFamily: Platform.select({
    ios: 'Courier',
    android: 'monospace',
    web: 'monospace',
  }),
  fontSize: 12,
  lineHeight: 18,
}

const copyValue = async (value: unknown) => {
  const text = JSON.stringify(value, null, 2) ?? String(value)
  if (Platform.OS === 'web') {
    await navigator.clipboard.writeText(text)
  } else {
    Share.share({ message: text })
  }
}

function Primitive({
  value,
}: {
  value: string | number | boolean | null | undefined
}) {
  if (value === null || value === undefined)
    return (
      <Text selectable style={mono} className="italic text-[#abb2bf]">
        {String(value)}
      </Text>
    )
  if (typeof value === 'string')
    return (
      <Text selectable style={mono} className="text-[#98c379]">
        {`"${value}"`}
      </Text>
    )
  if (typeof value === 'number')
    return (
      <Text selectable style={mono} className="text-[#61afef]">
        {String(value)}
      </Text>
    )
  return (
    <Text selectable style={mono} className="text-[#e5c07b]">
      {String(value)}
    </Text>
  )
}

function CollapsibleNode({
  label,
  openBracket,
  closeBracket,
  count,
  initialOpen = true,
  children,
}: {
  label?: string
  openBracket: string
  closeBracket: string
  count: number
  initialOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(initialOpen)

  return (
    <View>
      <Pressable onPress={() => setOpen((v) => !v)} hitSlop={8}>
        <Text selectable style={mono}>
          <Text className="text-[#61afef]">{open ? '▾' : '▸'} </Text>
          {label != null && <Text className="text-[#e06c75]">{label}</Text>}
          {label != null && <Text className="text-[#abb2bf]">{': '}</Text>}
          <Text className="text-[#abb2bf]">{openBracket} </Text>
          <Text className="text-[#4b5263]">{count}</Text>
          {!open && <Text className="text-[#abb2bf]">{` ${closeBracket}`}</Text>}
        </Text>
      </Pressable>
      {open && (
        <View className="border-l border-[#3e4451] pl-3">{children}</View>
      )}
      {open && (
        <Text selectable style={mono}>
          <Text className="text-[#61afef] opacity-0">{'▾ '}</Text>
          <Text className="text-[#abb2bf]">{closeBracket}</Text>
        </Text>
      )}
    </View>
  )
}

function JsonNode({
  value,
  label,
  depth = 0,
  initialOpen = true,
}: {
  value: JsonValue
  label?: string
  depth?: number
  initialOpen?: boolean
}) {
  if (value === null || value === undefined || typeof value !== 'object') {
    const primitive = (
      <Primitive
        value={value as string | number | boolean | null | undefined}
      />
    )
    if (label == null) return primitive
    return (
      <Text selectable style={mono}>
        <Text className="text-[#61afef] opacity-0">{'▾ '}</Text>
        <Text className="text-[#e06c75]">{label}</Text>
        <Text className="text-[#abb2bf]">{': '}</Text>
        {primitive}
      </Text>
    )
  }

  if (Array.isArray(value)) {
    if (value.length === 0)
      return (
        <Text selectable style={mono}>
          <Text className="text-[#61afef] opacity-0">{'▾ '}</Text>
          {label != null && (
            <Text className="text-[#e06c75]">
              {label}
              {': '}
            </Text>
          )}
          <Text className="text-[#abb2bf]">{'[]'}</Text>
        </Text>
      )
    return (
      <CollapsibleNode
        label={label}
        openBracket="["
        closeBracket="]"
        count={value.length}
        initialOpen={initialOpen}
      >
        {value.map((item, i) => (
          <JsonNode
            key={i}
            value={item}
            label={String(i)}
            depth={depth + 1}
            initialOpen={false}
          />
        ))}
      </CollapsibleNode>
    )
  }

  const entries = Object.entries(value)
  if (entries.length === 0)
    return (
      <Text selectable style={mono}>
        <Text className="text-[#61afef] opacity-0">{'▾ '}</Text>
        {label != null && (
          <Text className="text-[#e06c75]">
            {label}
            {': '}
          </Text>
        )}
        <Text className="text-[#abb2bf]">{'{}'}</Text>
      </Text>
    )

  return (
    <CollapsibleNode
      label={label}
      openBracket="{"
      closeBracket="}"
      count={entries.length}
      initialOpen={initialOpen}
    >
      {entries.map(([k, v]) => (
        <JsonNode
          key={k}
          value={v}
          label={k}
          depth={depth + 1}
          initialOpen={false}
        />
      ))}
    </CollapsibleNode>
  )
}

interface Props {
  value: unknown
  initialOpen?: boolean
}

export default function DebugJson({ value, initialOpen = true }: Props) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await copyValue(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <View className="rounded-lg bg-[#282c34] px-4 py-3">
      <Pressable
        onPress={handleCopy}
        hitSlop={8}
        className="absolute right-3.5 top-3 z-[1]"
      >
        <Text
          style={mono}
          className={cn('text-[#4b5263]', copied && 'text-[#98c379]')}
        >
          {copied ? 'copied ✓' : 'copy'}
        </Text>
      </Pressable>
      <JsonNode value={value as JsonValue} initialOpen={initialOpen} />
    </View>
  )
}
